import { useState, type ReactNode } from 'react';
import { inputClass } from './ui';

/**
 * Edição campo a campo da ficha: passar o mouse mostra ✏; clicar abre o input
 * com ✓ (salva) e ✕ (cancela). Esc cancela, Enter salva (exceto em texto longo).
 */
export function InlineEdit({
  label,
  value,
  display,
  onSave,
  type = 'text',
  multiline = false,
  placeholder = 'Insira um valor',
  disabled = false,
  renderInput,
}: {
  label: string;
  value: string | null | undefined;
  /** Como mostrar fora da edição (link, data formatada…). */
  display?: ReactNode;
  onSave: (value: string | null) => Promise<unknown>;
  type?: 'text' | 'email' | 'url' | 'date' | 'tel';
  multiline?: boolean;
  placeholder?: string;
  disabled?: boolean;
  /** Entrada customizada (ex.: campo adicional por tipo). */
  renderInput?: (draft: string, setDraft: (value: string) => void) => ReactNode;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const start = () => {
    if (disabled) return;
    setDraft(value ?? '');
    setError(null);
    setEditing(true);
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await onSave(draft.trim() === '' ? null : draft.trim());
      setEditing(false);
    } catch (saveError) {
      const response = (saveError as { response?: { data?: { message?: string | string[] } } })
        .response;
      const message = response?.data?.message;
      setError(
        Array.isArray(message) ? message.join(', ') : (message ?? 'Não foi possível salvar'),
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="group py-1.5 text-sm">
      <p className="text-[11px] font-medium uppercase tracking-wide text-slate-400">{label}</p>
      {editing ? (
        <div className="mt-1">
          <div className="flex items-start gap-1.5">
            <div className="min-w-0 flex-1">
              {renderInput ? (
                renderInput(draft, setDraft)
              ) : multiline ? (
                <textarea
                  autoFocus
                  rows={4}
                  className={`${inputClass} mt-0`}
                  value={draft}
                  onChange={(event) => setDraft(event.target.value)}
                  onKeyDown={(event) => event.key === 'Escape' && setEditing(false)}
                />
              ) : (
                <input
                  autoFocus
                  type={type}
                  className={`${inputClass} mt-0`}
                  value={draft}
                  onChange={(event) => setDraft(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') void save();
                    if (event.key === 'Escape') setEditing(false);
                  }}
                />
              )}
            </div>
            <button
              type="button"
              aria-label="Salvar"
              disabled={saving}
              onClick={() => void save()}
              className="rounded-lg bg-emerald-50 px-2 py-1.5 text-emerald-700 hover:bg-emerald-100 disabled:opacity-50"
            >
              ✓
            </button>
            <button
              type="button"
              aria-label="Cancelar"
              onClick={() => setEditing(false)}
              className="rounded-lg px-2 py-1.5 text-slate-400 hover:bg-slate-100"
            >
              ✕
            </button>
          </div>
          {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
        </div>
      ) : (
        <button
          type="button"
          onClick={start}
          disabled={disabled}
          className="flex w-full items-center justify-between gap-2 rounded-lg py-0.5 text-left disabled:cursor-default"
        >
          <span className={value ? 'text-slate-800' : 'text-slate-400'}>
            {value ? (display ?? value) : placeholder}
          </span>
          {!disabled && (
            <span
              className="text-xs text-slate-300 opacity-0 group-hover:opacity-100"
              aria-hidden
            >
              ✏
            </span>
          )}
        </button>
      )}
    </div>
  );
}
